import {
  createAction,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import {
  IndividualAnalysisRequest,
  IndividualEEGSearchRequest,
  reAnalysisIndividualNormApi,
  SearchedIndividualEEG,
  searchIndividualEEGApi,
} from 'api/analysisApi'
import {getDefaultPeriodString, periodStringToDate} from 'helpers/dateHelper'
import i18n from 'i18n'
import {RootState, ThunkExtraArguments} from 'store'
import {
  openSuccessModalAction,
  openFailureModalAction,
} from 'features/modal/alertSlice'
import {
  INDIVIDUAL_REANALYSIS_MANUAL_TYPE,
  TypeIndividualReanalysisManualType,
} from 'constants/AnalysisConstant'

export const searchIndividualEeg = createAsyncThunk(
  'api/eeg/individual/search',
  async (payload: IndividualEEGSearchRequest, {rejectWithValue, dispatch}) => {
    try {
      const response = await searchIndividualEEGApi(payload)
      return response.data
    } catch (err) {
      await dispatch(openFailureModalAction(err.message))
      return rejectWithValue(err.message)
    }
  },
)

export const openNormDialog = createAction<number>(
  'individualEeg/norm/dialog/open',
)

export const closeNormDialog = createAction('individualEeg/norm/dialog/close')

export const confirmNormDialog = createAsyncThunk<
  any,
  {eegId: number; zscore?: number},
  {extra: ThunkExtraArguments}
>(
  'individualEeg/norm/dialog/confirm',
  async (
    payload: {
      eegId: number
      zscore?: number
    },
    {rejectWithValue, dispatch},
  ) => {
    try {
      await reAnalysisIndividualNormApi(payload)
      await dispatch(openSuccessModalAction(i18n.t('ISuccess')))
      dispatch(closeNormDialog())
      return true
    } catch (err) {
      await dispatch(openFailureModalAction(err.message))
      return rejectWithValue(err.message)
    }
  },
)

interface IndividualEEGState {
  loading: boolean
  error: any
  items: SearchedIndividualEEG[]
  period: PeriodString
  search: AnalysisSearchKind
  paging: Paging
  oid?: string | null
  pagingInfo: {
    totalPages: number
    totalElements: number
  }
  normDialogOpen: boolean
  selectedId: number
  individualId: number
  agreeYn: YN | undefined
  manualType: TypeIndividualReanalysisManualType
  sraYn: YN | undefined
  isIcaSelectOpen: boolean
  isIcaConfirmOpen: boolean
  isIcaResultOpen: boolean
}

const initialState: IndividualEEGState = {
  loading: false,
  error: null,
  items: [],
  period: getDefaultPeriodString(),
  search: {searchName: 'NAME', searchValue: ''},
  paging: {page: 0, size: 10},
  pagingInfo: {
    totalPages: 1,
    totalElements: 0,
  },
  normDialogOpen: false,
  individualId: 0,
  selectedId: 0,
  agreeYn: undefined,
  manualType: INDIVIDUAL_REANALYSIS_MANUAL_TYPE.NONE,
  sraYn: undefined,
  isIcaSelectOpen: false,
  isIcaConfirmOpen: false,
  isIcaResultOpen: false,
  oid: '',
}

const slice = createSlice({
  name: 'eegIndividualSlice',
  initialState,
  reducers: {
    openIcaSelectDialog(state, action: PayloadAction<number>) {
      state.isIcaSelectOpen = true
      state.individualId = action.payload
    },
    confirmIcaSelectDialog(
      state,
      action: PayloadAction<IndividualAnalysisRequest>,
    ) {
      state.isIcaSelectOpen = false
      state.isIcaConfirmOpen = true

      if (action.payload.agreeYn !== undefined) {
        state.agreeYn = action.payload.agreeYn
      }

      state.manualType = action.payload.manualType

      if (action.payload.sraYn !== undefined) {
        state.sraYn = action.payload.sraYn
      }
    },
    closeIcaSelectDialog(state) {
      state.isIcaSelectOpen = false
      state.individualId = 0
      state.agreeYn = undefined
      state.manualType = INDIVIDUAL_REANALYSIS_MANUAL_TYPE.NONE
      state.sraYn = undefined
    },
    confirmIcaConfirmDialog(state) {
      state.isIcaConfirmOpen = false
      state.isIcaResultOpen = true
    },
    closeIcaConfirmDialog(state) {
      state.isIcaConfirmOpen = false
      state.individualId = 0
      state.agreeYn = undefined
      state.manualType = INDIVIDUAL_REANALYSIS_MANUAL_TYPE.NONE

      state.sraYn = undefined
    },
    confirmIcaResultDialog(state) {
      state.isIcaResultOpen = false
      state.individualId = 0
    },
    closeIcaResultDialog(state) {
      state.isIcaResultOpen = false
      state.individualId = 0
      state.agreeYn = undefined
      state.manualType = INDIVIDUAL_REANALYSIS_MANUAL_TYPE.NONE
      state.sraYn = undefined
    },
  },
  extraReducers: (builder) => {
    builder.addCase(openNormDialog, (state, action) => {
      state.normDialogOpen = true
      state.selectedId = action.payload
    })
    builder.addCase(closeNormDialog, (state) => {
      state.normDialogOpen = false
    })
    builder.addCase(searchIndividualEeg.pending, (state, action) => {
      state.loading = true
      state.error = null

      const {period, search, paging, oid} = action.meta.arg
      state.period = period
      state.search = search
      state.paging = paging
      state.oid = oid
    })
    builder.addCase(searchIndividualEeg.fulfilled, (state, action) => {
      state.loading = false

      const {
        content: items,
        pageable: {pageNumber: page},
        size,
        totalPages,
        totalElements,
      } = action.payload
      state.items = items
      state.loading = false
      state.error = null
      state.paging.page = page
      state.paging.size = size
      state.pagingInfo.totalPages = totalPages
      state.pagingInfo.totalElements = totalElements
    })
    builder.addCase(searchIndividualEeg.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })
  },
})

export default slice.reducer

export const {
  openIcaSelectDialog,
  confirmIcaSelectDialog,
  closeIcaSelectDialog,
  confirmIcaConfirmDialog,
  closeIcaConfirmDialog,
  confirmIcaResultDialog,
  closeIcaResultDialog,
} = slice.actions

export const selectLoading = (state: RootState) => state.eegIndividual.loading
export const selectError = (state: RootState) => state.eegIndividual.error
export const selectItems = (state: RootState) => state.eegIndividual.items
export const selectPeriod = (state: RootState) =>
  periodStringToDate(state.eegIndividual.period)
export const selectSearch = (state: RootState) => state.eegIndividual.search
export const selectPaging = (state: RootState) => state.eegIndividual.paging
export const selectPagingInfo = (state: RootState) =>
  state.eegIndividual.pagingInfo
export const selectQuery = (state: RootState) => ({
  period: state.eegIndividual.period,
  search: state.eegIndividual.search,
  paging: state.eegIndividual.paging,
  oid: state.eegIndividual.oid,
})
export const selectNormDialogOpen = (state: RootState) =>
  state.eegIndividual.normDialogOpen
export const selectSelectedId = (state: RootState) =>
  state.eegIndividual.selectedId
export const selectIndividualId = (state: RootState) =>
  state.eegIndividual.individualId
export const selectIcaSelectOpen = (state: RootState) =>
  state.eegIndividual.isIcaSelectOpen
export const selectIcaConfirmOpen = (state: RootState) =>
  state.eegIndividual.isIcaConfirmOpen
export const selectIcaResultOpen = (state: RootState) =>
  state.eegIndividual.isIcaResultOpen
export const selectReAnalysisYn = (state: RootState) => ({
  agreeYn: state.eegIndividual.agreeYn,
  manualType: state.eegIndividual.manualType,
  sraYn: state.eegIndividual.sraYn,
})
